import * as React from 'react';

import type * as api from '@gitbook/api-client';

import type { SiteCapability } from './permissions';

type SiteContextType = {
    site: api.Site;
    organizationId: string;
    analyticsContext: {
        site: string;
        organization: string;
    };
};

const SiteContext = React.createContext<SiteContextType | null>(null);

/**
 * Provide the site context.
 */
export function SiteContextProvider(props: {
    children: React.ReactNode;
    site: api.Site;
    organizationId: string;
}) {
    const { site, organizationId, children } = props;

    const value = React.useMemo(
        () => ({
            site,
            organizationId,
            analyticsContext: { site: site.id, organization: organizationId },
        }),
        [site, organizationId]
    );
    return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

/**
 * Hook to access the site context.
 */
export function useSiteContext() {
    const ctx = React.useContext(SiteContext);
    if (!ctx) {
        throw new Error('useSiteContext must be used within a SiteContextProvider');
    }
    return ctx;
}

/**
 * Context for the site capability.
 * We use a context for that to be warned if we try to access site capability outside of a SiteCapabilityProvider.
 * This way we can ensure that we don't fetch unintentionally the site capability for a site.
 * This is important because fetching the site capability is a costly operation.
 * If this cost is reduced in the future, we can remove this context.
 */
const SiteCapabilityContext = React.createContext<SiteCapability | null>(null);

export function SiteCapabilityProvider(props: {
    children: React.ReactNode;
    permissions: SiteCapability;
}) {
    const { permissions } = props;
    return (
        <SiteCapabilityContext.Provider value={permissions}>
            {props.children}
        </SiteCapabilityContext.Provider>
    );
}

/**
 * Hook to access the site capability of the current site.
 */
export function useSiteCapabilityContext() {
    const ctx = React.useContext(SiteCapabilityContext);
    if (!ctx) {
        throw new Error(
            process.env.NODE_ENV === 'development'
                ? `
useSiteCapabilityContext must be used within a SiteCapabilityProvider.
If you see this error, it probably means that you are trying to access the site capability in a list of sites.
This is a costly operation and should be avoided.`.trim()
                : 'useSiteCapabilityContext must be used within a SiteCapabilityProvider'
        );
    }
    return ctx;
}
